/**
 * tutorVideoQuestionAdapter — une question vidéo de la candidature (motivation, pédagogie,
 * technique), des tranches servies (`state.data.tutors.testQuestions` / `testAnswers`, remplies par
 * `getTestQuestions` et `submitTestVideo`) à ce que montrent la question et l'enregistrement.
 *
 * Port littéral de `tutor/application/motivation/InterviewScreen.js` (la liste des questions vidéo,
 * le champion) et de `InterviewQuestion.js` (la réponse, le temps de réflexion, la règle des
 * 10 minutes, ce qu'on peut terminer, ce qu'on montre) :
 *  - la réponse d'une question : la ligne de `testAnswers` de même `application_question_id` ;
 *  - « trop tard » : la question a été vue il y a plus de 10 minutes (`viewed_at`) ;
 *  - on peut terminer : une vidéo envoyée (`video`), une vidéo en cours de rangement
 *    (`answer` = `video_tmp:…`), ou trop tard ;
 *  - le temps de réflexion qui reste : la durée de la question moins le temps passé depuis la vue,
 *    jamais moins d'une seconde tant qu'on n'est pas reparti de zéro ;
 *  - le travail d'élève d'une question qui porte une activité : l'énoncé (la première pièce jointe
 *    du sujet) et la copie (la photo envoyée à l'étape 2 de l'activité), et la classe de l'élève.
 */
import moment from "moment"

import { type ChampionName } from "../../../../../design-system/atoms/Avatar/ChampionAvatar"
import {
    type UITutorVideoQuestion,
    type UITutorVideoQuestionPhase,
    type UITutorVideoQuestionWork,
} from "../../../ui/types/tutorCandidature"

export type ApiApplicationActivity = {
    student_class?: string | null
    material?: { attachments?: Array<string | null> | null } | null
    activity_logs?: Array<{ step?: number | null; action?: string | null; work_attachment?: string | null }> | null
}

export type ApiApplicationQuestion = {
    id?: number | string | null
    category?: string | null
    question?: string | null
    champion?: string | null
    answer_type?: string | null
    answer_duration?: number | null
    reflection_duration?: number | null
    activity?: ApiApplicationActivity | null
}

export type ApiApplicationAnswer = {
    id?: number | string | null
    application_question_id?: number | string | null
    viewed_at?: string | null
    answer?: string | null
    video?: string | null
}

/** La règle des 10 minutes : vue il y a plus longtemps, la question est passée. */
export const TOO_LATE_AFTER_SECONDS = 10 * 60

/** Les questions vidéo servies pour l'étape (`formData` de `InterviewScreen` : `answer_type === "video"`). */
export function videoQuestionsOf(
    questions: ApiApplicationQuestion[] | null | undefined,
    category: string
): ApiApplicationQuestion[] {
    const served = questions ?? []
    // L'ancien ne lisait la tranche que si elle était celle de l'étape (`questions[0].category`).
    if (served[0]?.category !== category) return []
    return served.filter((question) => question?.answer_type === "video")
}

/** Le champion qui pose la question : celui de la question servie, sinon celui de la motivation ou de la pédagogie (l'ancien `champion`). */
export function videoQuestionChampion(
    questions: ApiApplicationQuestion[] | null | undefined,
    category: string
): ChampionName | null {
    const served = questions?.[0]?.champion
    if (served === "tina" || served === "zoe" || served === "sam" || served === "abou") return served
    if (category === "motivation") return "tina"
    if (category === "educational") return "zoe"
    return null
}

export function answerOf(
    answers: ApiApplicationAnswer[] | null | undefined,
    questionId: number | string | null | undefined
): ApiApplicationAnswer | undefined {
    return (answers ?? []).find((answer) => String(answer?.application_question_id) === String(questionId))
}

export function isTooLate(answer: ApiApplicationAnswer | undefined, now: moment.Moment = moment()): boolean {
    return !!answer?.viewed_at && now.diff(answer.viewed_at, "seconds") > TOO_LATE_AFTER_SECONDS
}

/** Une vidéo est là : envoyée, ou en cours de rangement côté serveur. */
export function hasVideo(answer: ApiApplicationAnswer | undefined): boolean {
    return !!answer?.video || !!answer?.answer?.startsWith("video_tmp:")
}

export function canFinish(answer: ApiApplicationAnswer | undefined, now: moment.Moment = moment()): boolean {
    return hasVideo(answer) || isTooLate(answer, now)
}

/** `reflectionDuration` initial de `InterviewQuestion`. */
export function initialReflectionSeconds(
    question: ApiApplicationQuestion | undefined,
    answer: ApiApplicationAnswer | undefined,
    now: moment.Moment = moment()
): number {
    const duration = Number(question?.reflection_duration ?? 0)
    const elapsed = answer?.viewed_at ? now.diff(answer.viewed_at, "seconds") : 0
    return Math.max(duration - Math.min(elapsed, duration - 1), 0)
}

/** La phase de la question : l'envoi, la vidéo à revoir, trop tard, ou la réflexion. */
export function videoQuestionPhase(args: {
    answer: ApiApplicationAnswer | undefined
    uploading: boolean
    percent: number
    secondsLeft: number
    now?: moment.Moment
}): UITutorVideoQuestionPhase {
    if (args.uploading) return { kind: "uploading", percent: args.percent }
    if (hasVideo(args.answer)) return { kind: "recorded" }
    if (args.answer != null && isTooLate(args.answer, args.now)) return { kind: "tooLate" }
    return { kind: "thinking", secondsLeft: args.secondsLeft }
}

/** Le travail d'élève d'une question qui porte une activité, ou rien. */
export function videoQuestionWork(question: ApiApplicationQuestion | undefined): UITutorVideoQuestionWork | null {
    const activity = question?.activity
    if (activity == null) return null
    const copy = (activity.activity_logs ?? []).find(
        (log) => log?.step === 2 && log?.action === "step_media_sent"
    )?.work_attachment
    return {
        studentClass: activity.student_class ?? "",
        statementUri: activity.material?.attachments?.[0] ?? "",
        copyUri: copy ?? "",
    }
}

function category(served: string | null | undefined): UITutorVideoQuestion["category"] {
    if (served === "educational" || served === "technical") return served
    return "motivation"
}

export function toUIVideoQuestion(question: ApiApplicationQuestion | undefined): UITutorVideoQuestion | null {
    if (question == null) return null
    return {
        question: question.question ?? "",
        category: category(question.category),
        answerSeconds: Number(question.answer_duration ?? 0),
        // L'ancien montrait la copie pendant la réflexion pour une question technique seulement, et
        // pendant l'enregistrement pour toute question qui porte une activité : seules les
        // techniques en portent.
        work: videoQuestionWork(question),
    }
}

/**
 * La question telle que l'enregistrement la dit (`questionDisplay` de l'ancien `VideoRecordingScreen`) :
 * « (copie d'un élève en 2nde) » ajouté dès que la question porte une activité avec sa classe. La page
 * de la question compose le même titre elle-même (`videoQuestionTitle` du DS).
 */
export function recordingQuestionTitle(question: ApiApplicationQuestion | undefined): string {
    const text = question?.question ?? ""
    const studentClass = question?.activity?.student_class
    return studentClass ? `${text} (copie d'un élève en ${studentClass})` : text
}

/** Le nom du fichier envoyé depuis le téléphone (`InterviewQuestion.videoFile`). */
export function applicationVideoFileName(
    displayName: string | null | undefined,
    now: moment.Moment = moment()
): string {
    return `Masteur_Video_${displayName}_${now.format("YYYY-MM-DD")}.mp4`
}
